import { chowlyApi } from 'config'
import { generatePath } from 'react-router'

import client from '../chowly'
import {
  MenuItemSuspensionUpdateInput,
  MenuItemSuspensionUpdateResponse,
  MenuMappingItemResource,
  MenuMappingMenuResource,
  MenuMappingMenuResponse,
  MenuMappingSection,
  MenuMappingSectionInput,
  MenuMappingSectionResource,
  MenuMappingSectionResponse,
  MenuMappingSubsectionItemResource,
  MenuMappingSubsectionResource,
  MenuPublishResponse,
  OrderPlatformResource,
  TypeName,
  MenuMappingItemCustomizationResource,
  MenuMappingItemCustomization,
} from '../models'
import { getAvailabilities } from './helpers'
import { MenuMappingSectionFormatted } from './types'

type QueryParams = {
  include: string
}

export const getMenuMappingMenu = async (menuMappingMenuId: string, params?: QueryParams) => {
  const url = generatePath(chowlyApi.menuMappingMenu, { id: menuMappingMenuId })
  const { data } = await client.get<MenuMappingMenuResponse>(url, { params })
  return data
}

export const getMenuMappingSections = async (menuMappingMenuId: string) => {
  const url = generatePath(chowlyApi.menuMappingMenu, { id: menuMappingMenuId })
  try {
    const {
      data: { included },
    } = await client.get<MenuMappingMenuResponse>(url, { params: { include: 'sections' } })
    return (included ?? []).filter(
      (obj): obj is MenuMappingSectionResource => obj.type === TypeName.MenuMappingSection,
    )
  } catch (e) {
    return []
  }
}

/**
 * Maps item_customizations relationship identifiers to actual MenuMappingItemCustomization records
 */
const mapItemCustomizations = (
  itemCustomizationRefs: Array<{ id: string; type: string }> | undefined,
  included: Array<any> | undefined
): MenuMappingItemCustomization[] => {
  if (!itemCustomizationRefs?.length) return []
  
  return itemCustomizationRefs
    .map((itemCustomizationRef) => {
      const itemCustomization = (included ?? []).find(
        (obj): obj is MenuMappingItemCustomizationResource => 
          obj.type === TypeName.MenuMappingItemCustomization && obj.id === itemCustomizationRef.id
      )
      if (!itemCustomization) return undefined
      
      // Transform MenuMappingItemCustomizationResource to MenuMappingItemCustomization
      return {
        ...itemCustomization.attributes,
        id: itemCustomization.id,
        relationships: itemCustomization.relationships,
      } as MenuMappingItemCustomization
    })
    .filter((item): item is MenuMappingItemCustomization => item !== undefined)
}

export const getMenuMappingSection = async (
  menuMappingSectionId: string,
): Promise<MenuMappingSectionFormatted> => {
  const url = generatePath(chowlyApi.menuMappingSection, { id: menuMappingSectionId })
  const {
    data: { data, included },
  } = await client.get<MenuMappingSectionResponse>(url, {
    params: {
      include:
     'menu,menu.order_platform,subsections.items,subsections.subsection_items,subsections.items.child_items,subsections.items.child_items.child,service_availabilities.time_periods,subsections.items.item_customizations',
      'fields[order_platform]': 'partner_id',
    },
  })

  return {
    ...data,
    partnerId: included?.find(
      (obj): obj is OrderPlatformResource => obj.type === TypeName.OrderPlatform,
    )?.attributes?.partner_id,
    errors:
      included?.find((obj): obj is MenuMappingMenuResource => obj.type === TypeName.MenuMappingMenu)
        ?.attributes.errors ?? [],
    subsections: (included ?? [])
      .filter(
        (obj): obj is MenuMappingSubsectionResource => obj.type === TypeName.MenuMappingSubsection,
      )
      .map(({ id, type, attributes, relationships }) => ({
        type,
        ...attributes,
        id,
        items: relationships?.items?.data.length
          ? relationships.items.data.map(({ id, type }) => {
              const menuMappingItem = (included ?? []).filter(
                (obj): obj is MenuMappingItemResource => obj.id === id && obj.type === type,
              )[0]
              const menuMappingSubsectionItem = (included ?? []).filter(
                (obj): obj is MenuMappingSubsectionItemResource =>
                  obj.type === TypeName.MenuMappingSubsectionItem &&
                  menuMappingItem?.relationships.subsection_items.data.some(
                    ({ id }) => id === obj.id,
                  ),
              )[0]

              // Map item_customizations relationship identifiers to actual records
              const itemCustomizations = mapItemCustomizations(
                menuMappingItem?.relationships?.item_customizations?.data,
                included
              )

              return {
                id,
                type,
                ...menuMappingItem?.attributes,
                ...menuMappingSubsectionItem?.attributes,
                itemCustomizations,
                child_items: menuMappingItem?.relationships?.child_items?.data.length
                  ? menuMappingItem.relationships.child_items.data.map(({ id, type }) => {
                      const childItem = (included ?? []).filter(
                        (obj): obj is MenuMappingItemResource => 
                          obj.id === id && 
                          (obj.type === TypeName.MenuMappingItem || obj.type === TypeName.MenuMappingChildItem)
                      )[0]

                      const actualChildItem = childItem
                        ? (included ?? []).find(
                            (obj): obj is MenuMappingItemResource => 
                              obj.id === (childItem as any)?.relationships?.child?.data?.id && 
                              obj.type === TypeName.MenuMappingItem
                          )
                        : childItem

                      // Map item_customizations for child items using the reusable function
                      const childItemCustomizations = mapItemCustomizations(
                        actualChildItem?.relationships?.item_customizations?.data,
                        included
                      )

                      return {
                        id: actualChildItem?.id as string,
                        childItemId: id, // This is the child item ID from the relationships
                        type,
                        ...actualChildItem?.attributes,
                        ...childItem?.attributes,
                        itemCustomizations: childItemCustomizations,
                      }
                    })
                  : [],
              }
            })
          : [],
      })),
    availabilities: getAvailabilities(included),
  }
}

export const requestMenuPublish = async (
  menuMappingMenuId: string | undefined,
  onboarding?: boolean,
): Promise<MenuPublishResponse> => {
  const isOnboardingParam = onboarding ? '?is_onboarding=true' : ''

  return client.patch(
    `${generatePath(chowlyApi.publishUrl, {
      id: menuMappingMenuId,
    })}${isOnboardingParam}`,
    {},
  )
}

export const updateMenuItemSuspensionStatus = async (payload: MenuItemSuspensionUpdateInput) => {
  const { data } = await client.post<MenuItemSuspensionUpdateResponse>(
    chowlyApi.menuMappingSuspension,
    payload,
  )
  return data
}

export const createMenuMappingSection = async (payload: MenuMappingSectionInput) => {
  const { data } = await client.post(chowlyApi.menuMappingSections, payload)

  return data
}

export const formatMenuSectionPayload = (
  attributes: MenuMappingSection,
  menuId: MenuMappingMenuResource['id'],
): MenuMappingSectionInput => {
  return {
    data: {
      type: TypeName.MenuMappingSection,
      attributes,
      relationships: {
        menu: {
          data: {
            type: TypeName.MenuMappingMenu,
            id: menuId,
          },
        },
      },
    },
  }
}

/**
 * Update a menu section by ID.
 * @param menuMappingSectionId - The section's ID.
 * @param payload - The update payload (attributes, relationships).
 */
export const updateMenuMappingSection = async (
  menuMappingSectionId: string,
  payload: MenuMappingSectionInput
): Promise<MenuMappingSectionResource> => {
  const url = generatePath(chowlyApi.menuMappingSection, { id: menuMappingSectionId })
  const { data } = await client.patch<MenuMappingSectionResponse>(url, payload)
  return data.data
}

/**
 * Delete a menu section by ID.
 * @param menuMappingSectionId - The section's ID.
 */
export const deleteMenuMappingSection = async (menuMappingSectionId: string): Promise<void> => {
  const url = generatePath(chowlyApi.menuMappingSection, { id: menuMappingSectionId })
  await client.delete(url)
}
